import { NgModule, APP_INITIALIZER, ApplicationRef, DoBootstrap } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule } from '@angular/common/http';
import { HttpClientInMemoryWebApiModule } from 'angular-in-memory-web-api';
import { ClipboardModule } from 'ngx-clipboard';
import { TranslateModule } from '@ngx-translate/core';
import { ToastrModule } from 'ngx-toastr';
import { lastValueFrom } from 'rxjs';
import { StoreModule } from '@ngrx/store';
import { StoreRouterConnectingModule, routerReducer } from '@ngrx/router-store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { browserHttpInterceptorProviders } from '@donstudio/ds/interceptors';
import { ServiceWorkerModule, SwUpdate } from '@angular/service-worker';
import { environment } from 'src/environments/environment';
import { AppComponent } from './app.component';
import { AuthService } from './modules/auth/services/auth.service';
import { AppRoutingModule } from './app-routing';

// #locale-start#
// Register Persian
import { registerLocaleData } from '@angular/common';
import localeFa from '@angular/common/locales/fa';
import { extraLocaleFa } from '@donstudio/ds/functions';
registerLocaleData(extraLocaleFa, 'fa', localeFa)
// #locale-end#

// #fake-start#
import { FakeAPIService } from './_fake/fake-api.service';
// #fake-end#
// #tokens-start#
import { IS_BROWSER_PLATFORM, LOCATION } from '@donstudio/ds/_tokens';
// #tokens-end#

// #ag grid set the license
import { LicenseManager } from 'ag-grid-enterprise'
import { dailyServicePriceReducer, isChangedDataReducer, replaceRoomReducer, roomReducer } from '@pages/dashboard/reduce';
LicenseManager.setLicenseKey('IRDEVELOPERS_COM_NDEwMjM0NTgwMDAwMA==f08aae16269f416fe8d65bbf9396be5f');
// #ag grid set the license

// app initializer providers get user by token with factory
const getUserByToken = (authService: AuthService) => () => {
  return lastValueFrom(authService.getUserByToken());
}

// app initializer providers theme mode with factory
const themeModeSetup = (isBrowser: boolean) => () => {
  if (isBrowser) {
    const defaultThemeMode = 'system';
    const lsKey = 'ds_theme_mode_value';
    let themeMode = localStorage.getItem(lsKey);
    if (!themeMode) {
      if (defaultThemeMode === 'system') {
        themeMode = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      } else {
        themeMode = defaultThemeMode;
      }
    }

    document.documentElement.setAttribute('data-bs-theme', themeMode);
  }

  return Promise.resolve('Theme mode setup on page load');
}

// app initializer providers check for update scervice workers with factory
const swCheckForUpdate = (swUpdate: SwUpdate, location: Location) => () => {
  return new Promise(async (resolve, reject) => {
    try {
      if (swUpdate.isEnabled) {
        const isNewVersion = await swUpdate.checkForUpdate();
        if (isNewVersion) {
          const isNewVersionActivated = await swUpdate.activateUpdate();
          if (isNewVersionActivated) location.reload();
          resolve('A new version is available.');
        }
        resolve('Already on the latest version.');
      }
      resolve('Service workers is disabled');
    } catch (error) {
      location.reload();
    }
  });
}

// app initializer providers Back/forward cache with factory
const backForwardCacheSetup = (window: Window) => () => {
  let dbPromise: Promise<IDBDatabase> | null;
  function openDB() {
    if (!dbPromise) {
      dbPromise = new Promise((resolve, reject) => {
        const req = indexedDB.open('donstudio-db', 1);
        req.onupgradeneeded = () => req.result.createObjectStore('keyval');
        req.onerror = () => reject(req.error);
        req.onsuccess = () => resolve(req.result);
      });
    }
    return dbPromise;
  }

  // Close the connection to the database when the user is leaving.
  window.addEventListener('pagehide', () => {
    if (dbPromise) {
      dbPromise.then(db => db.close());
      dbPromise = null;
    }
  });

  // Open the connection when the page is loaded or restored from bfcache.
  window.addEventListener('pageshow', () => openDB());

  return Promise.resolve('Back forward cache setup on page load');
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule.withServerTransition({ appId: 'serverApp' }),
    BrowserAnimationsModule,
    TranslateModule.forRoot(),
    HttpClientModule,
    ClipboardModule,
    // #fake-start#
    environment.isMockEnabled
      ? HttpClientInMemoryWebApiModule.forRoot(FakeAPIService, {
        passThruUnknownUrl: true,
        dataEncapsulation: false,
      })
      : [],
    // #fake-end#
    AppRoutingModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerImmediately'
    }),
    // #ngrx-start#
    StoreModule.forRoot({
      router: routerReducer,
      roomData: roomReducer,
      replaceRoomData: replaceRoomReducer,
      dailyServicePrice: dailyServicePriceReducer,
      isChangedData: isChangedDataReducer
    }),
    StoreRouterConnectingModule.forRoot(),
    EffectsModule.forRoot([]),
    StoreDevtoolsModule.instrument({
      name: 'Donstudio store',
      logOnly: environment.production
    }),
    // #ngrx-end#
    ToastrModule.forRoot(),
  ],
  providers: [
    browserHttpInterceptorProviders,
    {
      provide: APP_INITIALIZER,
      useFactory: getUserByToken,
      multi: true,
      deps: [AuthService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: themeModeSetup,
      multi: true,
      deps: [IS_BROWSER_PLATFORM],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: swCheckForUpdate,
      multi: true,
      deps: [SwUpdate, LOCATION],
    },
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: backForwardCacheSetup,
    //   multi: true,
    //   deps: [WINDOW],
    // },
  ],
  bootstrap: [AppComponent],
})

export class AppModule implements DoBootstrap {

  ngDoBootstrap(appRef: ApplicationRef) {
    // const APP = AppComponent;
    // appRef.bootstrap(APP, document.getElementById('app'));
  }
}
